import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Update as UpdateIcon,
  Notifications as NotificationsIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import bookingService from '../../services/bookingService';
import api from '../../services/api';

const BookingDetailModal = ({ open, onClose, bookingId }) => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load booking details when modal opens
  useEffect(() => {
    if (open && bookingId) {
      loadBookingDetails();
    }
  }, [open, bookingId]);

  const loadBookingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bookingService.getBookingById(bookingId);
      setBooking(response);
    } catch (err) {
      console.error('Error loading booking details:', err);
      setError('Failed to load booking details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setBooking(null);
    setError(null);
    onClose();
  };

  const handleSendSMS = async () => {
    if (!booking?.customer?.phone) {
      setError('Customer has no phone number');
      return;
    }

    try {
      // Start conversation with customer's phone
      const response = await api.post('/v1/conversations/conversations/start_conversation/', {
        phone_number: booking.customer.phone,
        contact_name: booking.customer.name,
      });

      // Store conversation ID and navigate to chat
      sessionStorage.setItem('chat:openConversationId', response.data.conversation.id);
      handleClose();
      navigate('/chat');
    } catch (err) {
      setError('Error starting SMS conversation: ' + (err.response?.data?.error || err.message));
    }
  };

  // Helper functions
  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircleIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'updated':
        return <UpdateIcon color="warning" />;
      default:
        return <EventIcon />;
    }
  };

  const getStatusChip = (booking) => {
    if (booking.is_cancelled) {
      return <Chip label="CANCELLED" color="error" size="small" />;
    }

    const colorMap = {
      'confirmed': 'success',
      'assigned': 'info',
      'in_progress': 'primary',
      'waiting_parts': 'error',
      'pending_admin_review': 'secondary',
      'updated': 'warning',
      'completed': 'default',
    };

    return (
      <Chip
        label={booking.status.toUpperCase()}
        color={colorMap[booking.status] || 'default'}
        size="small"
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Booking Details
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {booking && (
          <Box>
            {/* Booking Header */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" mb={2}>
                {getStatusIcon(booking.status)}
                <Box ml={2}>
                  <Typography variant="h6">
                    {formatDate(booking.booking_date)}
                    {booking.time_of_day && ` - ${booking.time_of_day}`}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    {getStatusChip(booking)}
                    {booking.location && (
                      <Chip label={booking.location} variant="outlined" size="small" />
                    )}
                  </Box>
                </Box>
              </Box>

              {booking.is_cancelled && booking.cancellation_reason && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Cancellation Reason:</Typography>
                  {booking.cancellation_reason}
                </Alert>
              )}
            </Box>

            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center">
                      <PersonIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Customer Information</Typography>
                    </Box>
                    {booking.customer.phone && (
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={<SmsIcon />}
                        onClick={handleSendSMS}
                      >
                        Send SMS
                      </Button>
                    )}
                  </Box>
                  <Typography><strong>Name:</strong> {booking.customer.name}</Typography>
                  <Typography><strong>Email:</strong> {booking.customer.email || 'N/A'}</Typography>
                  <Typography><strong>Phone:</strong> {booking.customer.phone || 'N/A'}</Typography>
                  {booking.customer.address_line1 && (
                    <>
                      <Typography><strong>Address:</strong></Typography>
                      <Typography variant="body2" color="textSecondary">
                        {booking.customer.address_line1}
                        {booking.customer.city && `, ${booking.customer.city}`}
                        {booking.customer.postcode && ` ${booking.customer.postcode}`}
                      </Typography>
                    </>
                  )}
                </Paper>
              </Grid>

              {/* Vehicle Information */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <CarIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Vehicle Information</Typography>
                  </Box>
                  <Typography><strong>Registration:</strong> {booking.vehicle.registration}</Typography>
                  <Typography><strong>Make/Model:</strong> {booking.vehicle.make} {booking.vehicle.model}</Typography>
                  {booking.vehicle.year && (
                    <Typography><strong>Year:</strong> {booking.vehicle.year}</Typography>
                  )}
                  {booking.vehicle.fuel_type && (
                    <Typography><strong>Fuel Type:</strong> {booking.vehicle.fuel_type}</Typography>
                  )}
                  {booking.vehicle.engine_size && (
                    <Typography><strong>Engine Size:</strong> {booking.vehicle.engine_size}</Typography>
                  )}
                  {booking.vehicle.transmission && (
                    <Typography><strong>Transmission:</strong> {booking.vehicle.transmission}</Typography>
                  )}
                  {booking.vehicle.first_registered && (
                    <Typography><strong>First Registered:</strong> {formatDate(booking.vehicle.first_registered)}</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Services and Cost */}
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Services Required</Typography>
                  </Box>
                  {booking.work_required && booking.work_required.length > 0 ? (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Service</TableCell>
                              <TableCell align="right">Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {booking.work_required.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>{item.service}</TableCell>
                                <TableCell align="right">
                                  {item.cost ? formatCurrency(item.cost) : 'TBC'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Total Cost:</Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(booking.total_cost)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography color="textSecondary">No services specified</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Advisories */}
              {booking.advisories && booking.advisories.length > 0 && (
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <WarningIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6">MOT Advisories</Typography>
                    </Box>
                    {booking.advisories.map((advisory, index) => (
                      <Box key={index} mb={1}>
                        <Chip
                          label={advisory.severity}
                          color={
                            advisory.severity === 'MAJOR' ? 'error' :
                            advisory.severity === 'MINOR' ? 'warning' : 'info'
                          }
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2" component="span">
                          {advisory.description}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              )}

              {/* Notes */}
              {booking.notes && (
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" mb={1}>Notes</Typography>
                    <Typography variant="body2">{booking.notes}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Metadata */}
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" mb={1}>Extraction Metadata</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        <strong>Method:</strong> {booking.extraction_method}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        <strong>Created:</strong> {formatDateTime(booking.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        <strong>Updated:</strong> {formatDateTime(booking.updated_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        <strong>Booking ID:</strong> {booking.id}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingDetailModal;